import * as Sentry from "@sentry/react";

// Regex para CNPJ, CPF e e-mail — usado para scrub de PII em mensagens, exceptions e URLs.
const PII_RE =
  /\b(\d{3}\.?\d{3}\.?\d{3}-?\d{2}|\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}|[\w.+-]+@[\w-]+\.[\w.-]+)\b/g;

const scrub = (value: string | undefined): string | undefined =>
  value ? value.replace(PII_RE, "[REDACTED]") : value;

// Guard idempotente — Sentry.init pode ser chamado várias vezes (rotas privadas + consent
// granted na LP) e a chamada repetida sobrescreve o client e perde breadcrumbs.
let sentryInitialized = false;

export function initSentry() {
  if (sentryInitialized) return;
  if (!import.meta.env.VITE_SENTRY_DSN) {
    sentryInitialized = true;
    return;
  }

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],

    tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,

    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    sendDefaultPii: false,

    beforeSend(event) {
      if (event.message) {
        event.message = scrub(event.message) ?? event.message;
      }
      event.exception?.values?.forEach((value) => {
        if (value.value) {
          value.value = scrub(value.value) ?? value.value;
        }
      });
      if (event.request?.url) {
        event.request.url = scrub(event.request.url) ?? event.request.url;
      }
      if (event.request?.query_string) {
        event.request.query_string = "[REDACTED]";
      }
      return event;
    },

    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === "console") {
        const message = String(breadcrumb.message || "");
        if (/supabase|password|token|secret|key/i.test(message)) {
          return null;
        }
        if (breadcrumb.message) {
          breadcrumb.message = scrub(breadcrumb.message) ?? breadcrumb.message;
        }
      }

      if (breadcrumb.category === "fetch" || breadcrumb.category === "xhr") {
        if (breadcrumb.data?.url) {
          breadcrumb.data.url = scrub(breadcrumb.data.url) ?? breadcrumb.data.url;
        }
      }

      if (breadcrumb.category === "navigation") {
        if (breadcrumb.data?.from) {
          breadcrumb.data.from = scrub(breadcrumb.data.from) ?? breadcrumb.data.from;
        }
        if (breadcrumb.data?.to) {
          breadcrumb.data.to = scrub(breadcrumb.data.to) ?? breadcrumb.data.to;
        }
      }

      return breadcrumb;
    },
  });

  sentryInitialized = true;
}

export function setSentryUser(user: { id: string; email?: string }) {
  Sentry.setUser({ id: user.id, email: user.email });
}

export function clearSentryUser() {
  Sentry.setUser(null);
}

export function setSentryTenant(tenantId: string) {
  Sentry.setTag("tenant_id", tenantId);
}

export { Sentry };
